import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext.jsx';
import { RequireAuth } from './auth/RequireAuth.jsx';
import { I18nProvider } from './i18n/index.jsx';
import { AppShell } from './components/app/AppShell.jsx';

import { Home } from './pages/marketing/Home.jsx';
import { NotFound } from './pages/NotFound.jsx';
import { StyleGuide } from './pages/StyleGuide.jsx';
import { Privacy } from './pages/legal/Privacy.jsx';
import { Terms } from './pages/legal/Terms.jsx';

import { Account } from './pages/app/Account.jsx';
import { Dashboard } from './pages/app/Dashboard.jsx';
import { Forecast } from './pages/app/Forecast.jsx';
import { History } from './pages/app/History.jsx';
import { Login } from './pages/app/Login.jsx';
import { Pricing } from './pages/app/Pricing.jsx';
import { TopPicks } from './pages/app/TopPicks.jsx';
import { TrackRecord } from './pages/app/TrackRecord.jsx';

/*
 * The admin console is the ONLY lazily-loaded route, and deliberately so.
 *
 * A static import would bundle every admin screen, endpoint path and operational
 * string into the JavaScript that every marketing visitor downloads. Behind
 * React.lazy it becomes a genuine async chunk, fetched only by someone who
 * navigates to the console.
 *
 * This is a bundling boundary, not a security boundary — the real protection is
 * that every admin endpoint requires an admin JWT. Shipping the code to nobody
 * who doesn't need it is simply the right default.
 *
 * See the note in vite.config.js before adding a manualChunks rule for this.
 */
const AdminApp = lazy(() => import('./admin/AdminApp.jsx'));

/**
 * Routes.
 *
 *   /                 marketing site (public)
 *   /terms            Terms of Service (public)
 *   /privacy          Privacy Policy (public)
 *   /app/login        email one-time-code sign-in
 *   /app/*            the authenticated app, behind RequireAuth
 *   /style-guide      component catalogue (development reference)
 *
 * The legal pages are pinned to English alongside the marketing page: they are
 * only drafted in English, and showing a Russian speaker an English document
 * under a Russian heading would be worse than showing it plainly in English.
 */
export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <Routes>
          {/* Marketing is English-only for launch, so it is pinned rather than
              following the visitor's language preference. */}
          <Route
            path="/"
            element={
              <I18nProvider forceLang="en">
                <Home />
              </I18nProvider>
            }
          />
          <Route
            path="/terms"
            element={
              <I18nProvider forceLang="en">
                <Terms />
              </I18nProvider>
            }
          />
          <Route
            path="/privacy"
            element={
              <I18nProvider forceLang="en">
                <Privacy />
              </I18nProvider>
            }
          />
          <Route path="/style-guide" element={<StyleGuide />} />

          <Route path="/app/login" element={<Login />} />
          <Route
            path="/app"
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="forecast" element={<Forecast />} />
            <Route path="top-picks" element={<TopPicks />} />
            <Route path="history" element={<History />} />
            <Route path="track-record" element={<TrackRecord />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="account" element={<Account />} />
            <Route path="*" element={<Navigate to="/app" replace />} />
          </Route>

          {/*
            The admin console. Outside AuthProvider's influence — it brings its
            own AdminAuthProvider and its own token — and outside I18nProvider,
            since it is English-only for its single operator.

            Not linked from anywhere public: no nav entry, no sitemap, no
            robots.txt line (which would only advertise the path). It is
            reachable by typing it, and gated by the admin JWT on every call.
          */}
          <Route
            path="/admin/*"
            element={
              <Suspense fallback={null}>
                <AdminApp />
              </Suspense>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthProvider>
    </I18nProvider>
  );
}
